// src/App.tsx
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import AuthProvider from "./auth/AuthProvider";
import { useAuth } from "./auth/useAuth";
import Login from "./components/Login";
import Home from "./components/Home";
import DragAndDrop from "./components/DragAndDrop";
import Header from "./components/Header";
import RouteMemory from "./components/RouteMemory";
import WhyFilePigeon from "./components/WhyFilePigeon";
import { msalInstance } from "./msalInstance";

/** Handles the MSAL redirect response at /auth/callback, then sends user to /home */
function RedirectHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    msalInstance
      .handleRedirectPromise()
      .then((res) => {
        console.log("🔁 handleRedirectPromise result:", res);
        if (cancelled) {
          console.log("↪️ Redirect handling cancelled, aborting.");
          return;
        }
        if (res?.account) {
          console.log("🎯 Setting active account from redirect:", res.account.username);
          msalInstance.setActiveAccount(res.account);
        }
      })
      .catch((e) => {
        console.error("MSAL redirect error", e);
      })
      .finally(() => {
        console.log("➡️ Redirect handling complete.");
        if (!cancelled) {
          // Go to home after handling redirect
          navigate("/home", { replace: true });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // Optional small loading UI
  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      Processing sign-in...
    </div>
  );
}

/** Instant redirect for "/" to the last route (or /home if none). */
function ReturnToLastRoute() {
  const KEY = "__returnToRoute";
  let target = "/home";
  try {
    const r = sessionStorage.getItem(KEY);
    if (r && r !== "/") target = r;
  } catch {
    // ignore
  }
  return <Navigate to={target} replace />;
}

/** Protect routes: only render children if logged in, else redirect */
function PrivateRoute({ children }: { children: React.ReactElement }) {
  const { account } = useAuth();
  if (!account) {
    console.warn("🔒 Not logged in, redirecting to /login");
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function Frame() {
  const location = useLocation();
  const showHeader = location.pathname !== "/login";

  return (
    <div style={{ minHeight: "100vh", background: "var(--theme-tint-3)" }}>
      {/* Record and restore last route (no UI impact) */}
      <RouteMemory />

      {showHeader && <Header />}

      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login />} />

  {/* MSAL redirect callback */}
  <Route path="/auth/callback" element={<RedirectHandler />} />

        {/* Root -> jump back to last visited route or /home */}
        <Route path="/" element={<ReturnToLastRoute />} />

        {/* Protected */}
        <Route
          path="/home"
          element={
            <PrivateRoute>
              <Home />
            </PrivateRoute>
          }
        />
        <Route
          path="/why"
          element={
            <PrivateRoute>
              <WhyFilePigeon />
            </PrivateRoute>
          }
        />
        <Route
          path="/project/:id"
          element={
            <PrivateRoute>
              <DragAndDrop />
            </PrivateRoute>
          }
        />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <Frame />
      </AuthProvider>
    </Router>
  );
}
